// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import siteMessage from "@insite/client-framework/SiteMessage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import translate from "@insite/client-framework/Translate";
// import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal"; // Store Custom Code
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import { getCookie } from "@insite/mobius/utilities/cookies";
// import resetKeepMeLoggedInCookie from "@store/Framework/Store/Components/KeepMeLoggedInModal/Handlers/ResetKeepMeLoggedInCookie"; // Store Custom Code
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { useSession, useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { FC, useEffect, useRef, useState } from "react";

/* Store Custom Code - Start */
import { removeCookie } from "@insite/client-framework/Common/Cookies";
import { deleteSession, stopWatchingForOtherTabSessionChange } from "@insite/client-framework/Services/SessionService";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import resetKeepMeLoggedInCookie, {
    ResetKeepMeLoggedInCookieParameter,
} from "@store/Framework/Store/Components/KeepMeLoggedInModal/Handlers/ResetKeepMeLoggedInCookie";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import { css } from "styled-components";
import constants from "@store/constants";
import skipNavigationBlocker from "@store/Framework/Common/Utilities/skipNavigationBlocker";
import TwoButtonModal, { TwoButtonModalStyles } from "@store/Overrides/Components/TwoButtonModal";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
/* Store Custom Code - End */

export interface KeepMeLoggedInModalProps extends ExpandStyles<KeepMeLoggedInModalStyles> {}

export interface KeepMeLoggedInModalStyles {
    keepMeLoggedInModal?: TwoButtonModalStyles;
    // Store Custom Code
    title?: TypographyPresentationProps;
    content?: TypographyPresentationProps;
    countdown?: TypographyPresentationProps;
}

export const keepMeLoggedInModalStyles: KeepMeLoggedInModalStyles = {
    /* Store Custom Code - Start */
    keepMeLoggedInModal: {
        modal: {
            size: 415,
            cssOverrides: {
                modalContent: css`
                    padding: ${pxToRem(24)};
                `,
            },
        },
        submitButton: {
            sizeVariant: "large",
        },
        cancelButton: {
            sizeVariant: "large",
        },
        formActions: {
            spacing: pxToRem(24),
            extendedStyles: {
                flexWrapper: {
                    item: {
                        css: css`
                            flex-grow: 1;
                        `,
                    },
                },
            },
        },
    },
    title: {
        forwardAs: "p",
        css: css`
            ${spacingStyles(null, pxToRem(24))}
            font-size: ${pxToRem(20)};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
            text-align: center;
        `,
    },
    content: {
        css: css`
            ${spacingStyles(null, pxToRem(24))}
            text-align: center;
        `,
    },
    countdown: {
        forwardAs: "p",
        css: css`
            ${spacingStyles(null, pxToRem(24))}
            font-size: ${pxToRem(30)};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
            text-align: center;
        `,
    },
    /* Store Custom Code - End */
};

const keepMeLoggedInIntervalDelay = 10000;
const storefrontSessionCookieName = "StorefrontSessionExpiration";

/* Store Custom Code - Start */
const formatCountdown = (remainingMs: number): string => {
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

// Cross-tab notification that the session ended. The cookie/interval mechanism cannot promptly
// close the modal on a *visible* tab when another tab signs out (the platform other-tab watcher
// only reloads hidden tabs), so tabs broadcast an end-of-session event over localStorage.
const sessionEndedBroadcastKey = "greifSessionEnded";
type SessionEndedReason = "signout" | "expired";

const broadcastSessionEnded = (reason: SessionEndedReason): void => {
    if (typeof window === "undefined" || !window.localStorage) {
        return;
    }
    try {
        // The unique timestamp guarantees the value changes so a `storage` event fires in other tabs.
        window.localStorage.setItem(sessionEndedBroadcastKey, JSON.stringify({ reason, at: Date.now() }));
        // The event is already queued for other tabs; remove the key so nothing is left persisted.
        window.localStorage.removeItem(sessionEndedBroadcastKey);
    } catch {
        // localStorage can be unavailable (private mode/quota); cross-tab sync is best-effort.
    }
};
/* Store Custom Code - End */

const KeepMeLoggedInModal: FC<KeepMeLoggedInModalProps> = ({ extendedStyles, overridedStyles }) => {
    const styles = useExpandProps(keepMeLoggedInModalStyles, extendedStyles, overridedStyles);
    const [showKeepMeLoggedInModal, setShowKeepMeLoggedInModal] = useState(false);
    const { extensionWebsiteSettings } = useSettingsCollection();
    const enableSessionTimeoutWarningModal =
        extensionWebsiteSettings && extensionWebsiteSettings.enableSessionTimeoutWarningModal;
    const sessionTimeoutWarningModalTimeRemaining =
        extensionWebsiteSettings && extensionWebsiteSettings.sessionTimeoutWarningModalTimeRemaining;
    const keepModalClosedRef = useRef(false);
    const dispatch = useAppDispatch();
    const session = useSession();
    const toaster = useToaster();

    /* Store Custom Code - Start */
    const [countdown, setCountdown] = useState("");
    const isHandlingExpiry = useRef(false);
    const signInPageLink = usePageLinkByPageType(constants.pageType.signIn);
    const signInPageUrl = signInPageLink?.url;
    // Keep the resolved sign-in URL in a ref so effect-captured handlers (e.g. the cross-tab storage
    // listener) always read the latest value rather than a stale render's
    const signInPageUrlRef = useRef(signInPageUrl);
    useEffect(() => {
        signInPageUrlRef.current = signInPageUrl;
    }, [signInPageUrl]);

    const redirectToSignIn = (reason: SessionEndedReason) => {
        // The session is gone: the redirect must never wait on NavigationBlocker's unsaved-changes prompt.
        skipNavigationBlocker();
        const signInUrl = signInPageUrlRef.current;
        if (!signInUrl) {
            // The sign-in page link is not resolved (unexpected for an authenticated session); reload so the
            // server handles the now-unauthenticated state instead of guessing a hardcoded path.
            window.location.reload();
            return;
        }
        const returnUrl = window.location.pathname + window.location.search;
        const timeoutParam = reason === "expired" ? "timeout=true&" : "";
        window.location.href = `${signInUrl}?${timeoutParam}returnUrl=${encodeURIComponent(returnUrl)}`;
    };
    /* Store Custom Code - End */

    const keepMeLoggedInHandler = () => {
        setShowKeepMeLoggedInModal(false);
        /* Store Custom Code - Start */
        // The handler chain swallows API errors internally, so hook onException to detect a failed
        // refresh (e.g. the session was already ended elsewhere) and route to the expiry flow instead
        // of leaving the user with a false "still signed in" state.
        dispatch(
            resetKeepMeLoggedInCookie({
                onException: () => signOutHandler(),
            } as ResetKeepMeLoggedInCookieParameter),
        );
        /* Store Custom Code - End */
    };

    const signOutHandler = () => {
        /* Store Custom Code - Start */
        if (isHandlingExpiry.current) {
            return;
        }
        isHandlingExpiry.current = true;
        setShowKeepMeLoggedInModal(false);

        broadcastSessionEnded("expired");
        stopWatchingForOtherTabSessionChange();
        removeCookie("NavigationMode");
        deleteSession().finally(() => {
            redirectToSignIn("expired");
        });
        /* Store Custom Code - End */
    };

    const closeHandler = () => {
        keepModalClosedRef.current = true;
        setShowKeepMeLoggedInModal(false);
        /* Store Custom Code - Start */
        broadcastSessionEnded("signout");
        stopWatchingForOtherTabSessionChange();
        removeCookie("NavigationMode");
        deleteSession().finally(() => {
            redirectToSignIn("signout");
        });
        /* Store Custom Code - End */
    };

    useEffect(() => {
        if (
            !enableSessionTimeoutWarningModal ||
            !sessionTimeoutWarningModalTimeRemaining ||
            !session.isAuthenticated ||
            session.isGuest
        ) {
            return;
        }

        /* Store Custom Code - Start */
        const checkSession = () => {
            const storefrontSessionExpirationCookie = getCookie(storefrontSessionCookieName);

            if (!storefrontSessionExpirationCookie) {
                return;
            }

            const timeDifferences = Number(storefrontSessionExpirationCookie) - Date.now();

            if (timeDifferences <= 0) {
                signOutHandler();
                return;
            }

            if (timeDifferences <= sessionTimeoutWarningModalTimeRemaining) {
                if (!keepModalClosedRef.current) {
                    setShowKeepMeLoggedInModal(true);
                }
            } else {
                // The session was refreshed (e.g. another tab clicked "Stay Signed In", which
                // resets the shared StorefrontSessionExpiration cookie) and is back within safe
                // parameters; close the modal if a previous tick opened it, and re-arm the warning.
                setShowKeepMeLoggedInModal(false);
                keepModalClosedRef.current = false;
            }
        };

        const keepMeLoggedInInterval = setInterval(checkSession, keepMeLoggedInIntervalDelay);

        // Hidden tabs throttle timers, so re-check immediately when the tab becomes visible to
        // promptly reflect a session refresh made in another tab instead of waiting for the interval.
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                checkSession();
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);

        // React when another tab ends the session (sign-out or expiry). This covers visible tabs the
        // platform other-tab watcher will not reload, so this tab stops running against a dead session.
        const handleSessionEndedBroadcast = (event: StorageEvent) => {
            if (event.key !== sessionEndedBroadcastKey || !event.newValue || isHandlingExpiry.current) {
                return;
            }
            isHandlingExpiry.current = true;
            keepModalClosedRef.current = true;
            setShowKeepMeLoggedInModal(false);
            stopWatchingForOtherTabSessionChange();
            removeCookie("NavigationMode");

            let reason: SessionEndedReason = "expired";
            try {
                reason = JSON.parse(event.newValue).reason === "signout" ? "signout" : "expired";
            } catch {
                // Malformed payload; fall back to the expiry flow.
            }

            // The originating tab already deleted the server session, so just navigate away here.
            redirectToSignIn(reason);
        };
        window.addEventListener("storage", handleSessionEndedBroadcast);
        /* Store Custom Code - End */

        return () => {
            clearInterval(keepMeLoggedInInterval);
            /* Store Custom Code - Start */
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("storage", handleSessionEndedBroadcast);
            /* Store Custom Code - End */
        };
    }, [enableSessionTimeoutWarningModal, session.isAuthenticated]);

    /* Store Custom Code - Start */
    useEffect(() => {
        if (!showKeepMeLoggedInModal) {
            setCountdown("");
            return;
        }

        const tick = () => {
            const cookieValue = getCookie(storefrontSessionCookieName);
            if (!cookieValue) {
                return;
            }

            const remainingMs = Number(cookieValue) - Date.now();

            if (remainingMs <= 0) {
                signOutHandler();
                return;
            }

            setCountdown(formatCountdown(remainingMs));
        };

        tick();
        const ticker = setInterval(tick, 1000);
        return () => clearInterval(ticker);
    }, [showKeepMeLoggedInModal]);

    const title = translate("Session expiring soon");
    /* Store Custom Code - End */

    return (
        <TwoButtonModal
            extendedStyles={styles.keepMeLoggedInModal} // Store Custom Code
            modalIsOpen={showKeepMeLoggedInModal}
            headlineText={title} // Store Custom Code
            /* Store Custom Code - Start */
            messageText={
                <>
                    <Typography {...styles.title}>{title}</Typography>
                    <StyledWrapper {...styles.content}>
                        {siteMessage("Session_Timeout_Warning_Modal_Message")}
                    </StyledWrapper>
                    {countdown ? <Typography {...styles.countdown}>{countdown}</Typography> : null}
                </>
            }
            /* Store Custom Code - End */
            cancelButtonText={translate("Sign Out Now")} // Store Custom Code
            submitButtonText={translate("Stay Signed In")} // Store Custom Code
            onCancel={closeHandler}
            onSubmit={keepMeLoggedInHandler}
            /* Store Custom Code - Start */
            dataTestSelector="keepMeLoggedInModal"
            cancelTestSelector="keepMeLoggedInModal_signOutNow"
            submitTestSelector="keepMeLoggedInModal_staySignedIn"
            hideTitle
            isCloseable={false}
            /* Store Custom Code - End */
        />
    );
};

export default KeepMeLoggedInModal;
